import { ApiAlertResponse } from "@incendium/api";
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  InputAdornment,
} from "@mui/material";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import { GenericDialoagProps } from "Interfaces";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";
import { FormEvent, useEffect, useState } from "react";
import { Box } from "@mui/system";
import produce from "immer";
import AnimatedList from "Components/AnimatedList/AnimatedList";
import { Email } from "@mui/icons-material";

function AlertCommsRow({
  row,
  onEdit,
}: {
  row: { email: string };
  onEdit: (v: { email: string }) => void;
}) {
  return (
    <Box sx={{ width: "100%" }}>
      <TextValidator
        name="email"
        validators={["required", "isEmail"]}
        errorMessages={["Email is required", "Email is not valid"]}
        fullWidth
        label="Email"
        value={row.email}
        onChange={(e) =>
          onEdit({ email: (e.target as HTMLInputElement).value })
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Email fontSize="small" color="primary" />
            </InputAdornment>
          ),
        }}
      />
    </Box>
  );
}

interface IAlertCommsDialogProps extends GenericDialoagProps {
  alert: ApiAlertResponse;
  setAlert: React.Dispatch<React.SetStateAction<ApiAlertResponse | undefined>>;
}

function AlertCommsDialog({
  open,
  setOpen,
  alert,
  setAlert,
}: IAlertCommsDialogProps) {
  const [recipients, setRecipients] = useState<{ email: string }[]>(
    alert.emails?.length
      ? alert.emails.map((e) => ({ email: e }))
      : [{ email: "" }],
  );

  useEffect(() => {
    if (open) {
      setRecipients(
        alert.emails?.length
          ? alert.emails.map((e) => ({ email: e }))
          : [{ email: "" }],
      );
    }
  }, [open, alert.emails]);

  const save = (e: FormEvent<Element>) => {
    e.preventDefault();
    e.stopPropagation();
    setAlert(
      produce(alert, (draft) => {
        draft.emails = recipients.filter((r) => !!r).map((r) => r.email);
      }),
    );
    setOpen(false);
    return;
  };

  const onClose = () => {
    setRecipients(
      alert.emails?.length
        ? alert.emails.map((e) => ({ email: e }))
        : [{ email: "" }],
    );
    setOpen(false);
  };

  const onAdd = () => {
    setRecipients(
      produce(recipients, (draft) => {
        draft.push({ email: "" });
      }),
    );
  };

  const onRemove = (idx: number) => {
    setRecipients(
      produce(recipients, (draft) => {
        draft.splice(idx, 1);
      }),
    );
  };

  const onEdit = (idx: number) => (value: any) => {
    setRecipients(
      produce(recipients, (draft) => {
        draft[idx] = value;
      }),
    );
  };

  return (
    <CustomDialog open={open} fullWidth maxWidth="sm">
      <DialogTitle>Select new comms method for {alert.name}</DialogTitle>
      <ValidatorForm onSubmit={save} name="comms">
        <DialogContent>
          <Box mb={2}>
            <AnimatedList<{ email: string }, any>
              onAdd={onAdd}
              onRemove={onRemove}
              onEdit={onEdit}
              items={recipients || []}
              RowComponent={AlertCommsRow}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary">
            Cancel
          </Button>
          <Button variant="contained" color="primary" type="submit">
            Save
          </Button>
        </DialogActions>
      </ValidatorForm>
    </CustomDialog>
  );
}

export default AlertCommsDialog;
